// src/pages/login/biometric-login/biometric-login.tsx
/** @jsxImportSource @emotion/react */
import type React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import biometricLoginSvg from '/biometric-login.svg';
import { VSpace } from '../../chat/settings/account/account-settings';
import { UniversalSpinner } from '../../../components/universal-spinner/universal-spinner';
import type { LoginStepProps } from '../login-shell.types';
import {
  AnimatedIconWrap,
  ErrorText,
  Screen,
  SpinnerWrap,
  StatusText,
} from './biometric-login.styled';
import { useBiometricLogin } from './biometric-login.hooks';
import { useAuth } from '../../../contexts/auth/auth-context';
import { readStoredSession } from '../../../api/token-storage';
import { isNative } from '../../../platform';

function makeDiagId(prefix = 'bio_login') {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * Synchronous pre-flight check: can WebAuthn even work here?
 * Returns null if OK, or an error string explaining why not.
 */
function preflightCheck(): string | null {
  // Native Capacitor uses the native biometric plugin, not WebAuthn
  if (isNative()) return null;

  if (!window.isSecureContext) {
    const host = location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') {
      return `Biometric login requires HTTPS. Current origin (${location.origin}) is not a secure context. Use HTTPS or localhost.`;
    }
  }
  if (typeof PublicKeyCredential === 'undefined') {
    return 'WebAuthn is not supported in this browser. Biometric login is unavailable.';
  }
  return null;
}

export const BiometricLogin: React.FC<LoginStepProps> = (props) => {
  const { replace, setData } = props;

  const diagId = useMemo(() => makeDiagId(), []);
  const hasNavigatedRef = useRef(false);

  // StrictMode-safe: ensure we only auto-start once per mount
  const didAutoStartRef = useRef(false);
  const didRouteErrorRef = useRef(false);

  // Synchronous pre-flight: check immediately, don't waste time in async flow
  const preflightError = useMemo(() => preflightCheck(), []);

  const navigate = useNavigate();
  const { biometricLogin } = useAuth();

  const { loading, error, diag, start } = useBiometricLogin({
    autoStart: false, // we will auto-start safely below
    diagId,

    // ✅ IMPORTANT: do NOT use conditional UI (autofill) on this screen
    // It can hang forever unless a focused input is present.
    preferConditionalUI: false,

    onSuccess: async (payload) => {
      try {
        await biometricLogin(payload.access_token, payload.refresh_token);

        if (!hasNavigatedRef.current) {
          hasNavigatedRef.current = true;
          navigate('/chat', { replace: true });
        }
      } catch (err: any) {
        console.error('[BiometricLogin] biometricLogin() failed after WebAuthn success:', err);
        // Don't strand the user — let them fall through to password login
        if (!hasNavigatedRef.current) {
          hasNavigatedRef.current = true;
          replace('email-login');
        }
      }
    },
  });

  // If prerequisites are not met, route to error immediately with context
  useEffect(() => {
    if (preflightError && !didRouteErrorRef.current) {
      console.error('[BiometricLogin] preflight failed:', preflightError);
      didRouteErrorRef.current = true;
      setData({ biometricPreflightError: preflightError });
      replace('biometric-error');
    }
  }, [preflightError, replace, setData]);

  useEffect(() => {
    const stored = readStoredSession();
    if (stored?.access_token && stored?.refresh_token && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      navigate('/chat', { replace: true });
    }
  }, [navigate]);

  // ✅ Safe auto-start — only if preflight passed
  useEffect(() => {
    if (preflightError) return; // skip if prerequisites not met
    if (didAutoStartRef.current) return;
    if (hasNavigatedRef.current) return;

    didAutoStartRef.current = true;

    const t = window.setTimeout(() => {
      void start();
    }, 0);

    return () => window.clearTimeout(t);
  }, [start, preflightError]);

  // Route to biometric-error exactly once when the attempt finished with an error
  useEffect(() => {
    if (loading) return;
    if (!error) return;
    if (didRouteErrorRef.current) return;

    didRouteErrorRef.current = true;
    replace('biometric-error');
  }, [error, loading, replace]);

  // Derive a user-friendly status from the diagnostic step
  const stepLabel = useMemo(() => {
    const step = diag?.step;
    if (!loading) return null;
    switch (step) {
      case 'capability-check': return 'Checking device capabilities…';
      case 'fetch-authentication-options': return 'Contacting server…';
      case 'start-authentication': return 'Waiting for biometric prompt…';
      case 'verify-authentication': return 'Verifying with server…';
      default: return 'Verifying biometrics…';
    }
  }, [loading, diag?.step]);

  return (
    <Screen data-testid="biometric-login-screen">
      <VSpace $h={187} />

      <AnimatedIconWrap
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.17, 0.67, 0.83, 0.67] }}
      >
        <AnimatedIconWrap
          animate={{
            y: [0, -6, 0],
            rotate: [0, -1.2, 1.2, 0],
            filter: [
              'drop-shadow(0px 0px 0px rgba(94,132,237,0.0))',
              'drop-shadow(0px 0px 14px rgba(94,132,237,0.25))',
              'drop-shadow(0px 0px 0px rgba(94,132,237,0.0))',
            ],
          }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img style={{ width: '64px' }} src={biometricLoginSvg} alt="Biometric login" />
        </AnimatedIconWrap>
      </AnimatedIconWrap>

      <VSpace $h={57} />

      <StatusText>{loading ? (stepLabel || 'Verifying biometrics…') : 'Continue with biometrics'}</StatusText>

      {loading && (
        <SpinnerWrap data-testid="biometric-login-loading">
          <UniversalSpinner />
        </SpinnerWrap>
      )}

      {!loading && (
        <>
          {error && <ErrorText data-testid="biometric-login-error">{error}</ErrorText>}

          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={() => void start()}
              style={{
                cursor: 'pointer',
                borderRadius: 14,
                padding: '10px 14px',
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(94,132,237,0.14)',
                color: 'rgba(255,255,255,0.92)',
                fontWeight: 700,
              }}
            >
              {error ? 'Retry' : 'Use biometrics'}
            </button>

            <button
              type="button"
              onClick={() => replace('email-login')}
              style={{
                cursor: 'pointer',
                borderRadius: 14,
                padding: '10px 14px',
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,255,255,0.86)',
                fontWeight: 700,
              }}
            >
              Use password
            </button>
          </div>

        </>
      )}
    </Screen>
  );
};
